import { AuthLayout, Button, InlineMessage, Spinner, Stack, Text } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { neonAuthBaseUrl } from './adapter';
import { authFrame } from './AuthGate';
import { useCurrentUser } from './AuthProvider';
import { oauthQueryOf, onlyLoopback } from './oauthQuery';

// Neon Auth's OAuth provider (docs: neon.com/docs/auth/guides/plugins/oauth-provider) sends an MCP
// client's sign-in here: /oauth/login when the person has no Neon Auth session, then
// /oauth/consent to approve the client. Both pages sit inside AuthGate, so only someone Mothership
// admits reaches them; the MCP endpoint admits them again on every call (apps/server/src/mcp.mjs).
// Each page hands Neon Auth back its signed authorization request exactly as received.

async function postToNeonAuth(path: string, body: Record<string, unknown>): Promise<string> {
  if (neonAuthBaseUrl === undefined) throw new Error('Sign-in is not configured for this environment.');
  const response = await fetch(`${neonAuthBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const answer: unknown = await response.json().catch(() => null);
  const url = typeof answer === 'object' && answer !== null && 'url' in answer ? (answer as { url: unknown }).url : undefined;
  if (!response.ok || typeof url !== 'string') {
    const message = typeof answer === 'object' && answer !== null && 'message' in answer ? String((answer as { message: unknown }).message) : '';
    throw new Error(message || `Neon Auth answered ${response.status}.`);
  }
  return url;
}

/** /oauth/login: the person is signed in (AuthGate saw to that), so the authorization request carries on. */
export function OAuthLoginPage(): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const oauthQuery = oauthQueryOf(window.location.search);
    postToNeonAuth('/oauth2/continue', { selected: true, oauth_query: oauthQuery })
      .then((url) => window.location.assign(url))
      .catch((failure: unknown) => setError(failure instanceof Error ? failure.message : 'Could not continue.'));
  }, []);

  return (
    <AuthLayout {...authFrame} title="Connecting your assistant" description="Signing you in to Mothership for an MCP client.">
      {error === null ? (
        <Spinner label="Continuing…" />
      ) : (
        <InlineMessage tone="error" role="alert">
          {error}
        </InlineMessage>
      )}
    </AuthLayout>
  );
}

interface ClientDetails {
  name: string;
  clientUri: string | null;
}

/** /oauth/consent: who is asking, where the code goes, and what they get, then Allow or Deny. */
export function OAuthConsentPage(): JSX.Element {
  const user = useCurrentUser();
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') ?? '';
  const redirectUris = params.getAll('redirect_uri');
  const scopes = (params.get('scope') ?? '').split(' ').filter(Boolean);
  const resource = params.get('resource');
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (neonAuthBaseUrl === undefined || clientId === '') return;
    fetch(`${neonAuthBaseUrl}/oauth2/public-client?${new URLSearchParams({ client_id: clientId }).toString()}`, { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { client_name?: string; client_uri?: string } | null) =>
        setClient({ name: body?.client_name ?? 'An unnamed client', clientUri: body?.client_uri ?? null }),
      )
      .catch(() => setClient({ name: 'An unnamed client', clientUri: null }));
  }, [clientId]);

  async function respond(accept: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await postToNeonAuth('/oauth2/consent', { accept, oauth_query: oauthQueryOf(window.location.search) }));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not record your answer.');
      setBusy(false);
    }
  }

  const hosts = redirectUris.map((uri) => {
    try {
      return new URL(uri).host;
    } catch {
      return uri;
    }
  });

  return (
    <AuthLayout
      {...authFrame}
      title="Allow access to Mothership?"
      description={`${client?.name ?? 'A client'} wants to read Mothership as ${user.name}: what you can see in the app, nothing more.`}
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Text size="sm">Client: {client?.name ?? '…'}{client?.clientUri ? ` (${new URL(client.clientUri).host})` : ''}</Text>
          <Text size="sm">Sends you back to: {hosts.join(', ') || 'unknown'}</Text>
          <Text size="sm">Access: {scopes.join(', ') || 'none named'}{resource ? ` for ${resource}` : ''}</Text>
        </Stack>
        {onlyLoopback(redirectUris) ? (
          <InlineMessage tone="warning" role="status">
            This client runs on your own computer. Allow it only if you just started it, for example by adding Mothership to Claude Code.
          </InlineMessage>
        ) : null}
        {error === null ? null : (
          <InlineMessage tone="error" role="alert">
            {error}
          </InlineMessage>
        )}
        <Stack gap="sm">
          <Button variant="brand" loading={busy} onClick={() => void respond(true)}>
            Allow
          </Button>
          <Button variant="subtle" disabled={busy} onClick={() => void respond(false)}>
            Deny
          </Button>
        </Stack>
      </Stack>
    </AuthLayout>
  );
}
